import type { SvgIconComponent } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import HelpIcon from "@mui/icons-material/HelpOutlineRounded";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import WarningIcon from "@mui/icons-material/WarningAmberRounded";
import { Box, Collapse, Skeleton, Stack, Typography } from "@mui/material";
import { blue, deepPurple, orange, purple } from "@mui/material/colors";
import deepEqual from "fast-deep-equal";
import { useCallback, useState } from "react";
import ShowMoreLessButton from "@entrilia/shared/components/ShowMoreLessButton";
import StatusChip from "@entrilia/shared/components/StatusChip";
import useFetch from "@entrilia/shared/hooks/useFetch";
import useSetInterval from "@entrilia/shared/hooks/useSetInterval";
import useToggleState from "@entrilia/shared/hooks/useToggleState";
import SparklesIcon from "@entrilia/shared/icons/SparklesIcon";
import { logError } from "@entrilia/shared/logging";
import type { ThemeColorFn } from "@entrilia/shared/utilities/colorHelper";
import { makeModeAwareAccent, makeOpaqueTintedBackgroundFromColor } from "@entrilia/shared/utilities/colorHelper";
import adminApi from "../../../../api/adminApi";
import type {
  GetInvoiceAgentResponse,
  InvoiceAgentState,
  InvoiceAgentStatus,
} from "../../../../api/types/expenseManagementTypes";
import BetaLabel from "../../../common/BetaLabel";
import InvoiceAgentInvoicesTable from "./InvoiceAgentInvoicesTable";

const POLL_INTERVAL_MS = 3_000;

const MAX_POLLS = Math.ceil(120_000 / POLL_INTERVAL_MS);

const MAX_RECHECK_POLLS = Math.ceil(30_000 / POLL_INTERVAL_MS);

const checkStatusIcons: Record<InvoiceAgentStatus, SvgIconComponent> = {
  Alert: WarningIcon,
  Warning: VisibilityIcon,
  Ok: CheckCircleIcon,
  Unknown: HelpIcon,
};

const overallStatusLabels: Partial<Record<InvoiceAgentStatus, string>> = {
  Alert: "Needs attention",
  Warning: "Worth a look",
  Ok: "Looks usual",
};

const checkStatusColors: Record<InvoiceAgentStatus, ThemeColorFn> = {
  Alert: (t) => (t.palette.mode === "dark" ? t.palette.error.light : t.palette.error.main),
  Warning: (t) => t.palette.warning.main,
  Ok: (t) => (t.palette.mode === "dark" ? t.palette.success.light : t.palette.success.main),
  Unknown: (t) => t.palette.text.secondary,
};

const uncheckedSummaries: Record<Exclude<InvoiceAgentState, "Checked">, string> = {
  Calculating: "Checking this invoice against the vendor's earlier invoices…",
  NotChecked: "The Invoice Agent has not checked this invoice. It checks invoices when they are created or edited.",
  NoVendor: "This invoice has no vendor yet, so there is nothing to compare it with.",
};

const borderGradient = `linear-gradient(90deg, ${blue[400]}, ${purple[300]}, ${orange[400]})`;

const getSummary = (invoiceAgent: GetInvoiceAgentResponse | undefined, hasFailed: boolean) => {
  if (hasFailed) {
    return "The Invoice Agent's check could not be loaded. Reload the page to try again.";
  }

  if (!invoiceAgent) {
    return undefined;
  }

  return invoiceAgent.state === "Checked"
    ? [invoiceAgent.summary, ...invoiceAgent.observations].join(" ")
    : uncheckedSummaries[invoiceAgent.state];
};

const isAwaitingCheck = (
  invoiceAgent: GetInvoiceAgentResponse | undefined,
  shownBeforeSave: GetInvoiceAgentResponse | undefined,
  pollCount: number
): boolean => {
  if (pollCount >= MAX_POLLS) {
    return false;
  }

  if (invoiceAgent?.state === "Calculating") {
    return true;
  }

  return shownBeforeSave !== undefined && pollCount < MAX_RECHECK_POLLS && deepEqual(invoiceAgent, shownBeforeSave);
};

interface Props {
  invoiceId: string;
  invoiceUpdateAt?: string;
}

const InvoiceAgent = ({ invoiceId, invoiceUpdateAt }: Props) => {
  const [expanded, toggleExpanded] = useToggleState(false);
  const [pollCount, setPollCount] = useState(0);
  const [prevInvoiceUpdateAt, setPrevInvoiceUpdateAt] = useState(invoiceUpdateAt);
  const [shownBeforeSave, setShownBeforeSave] = useState<GetInvoiceAgentResponse>();

  const getInvoiceAgent = useCallback(
    () => adminApi.getInvoiceAgent(invoiceId),
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- refetch right after a save, while the server still answers "Calculating"
    [invoiceId, invoiceUpdateAt]
  );

  const [invoiceAgent, fetchError, { fetchSilent }] = useFetch(getInvoiceAgent);

  const poll = useCallback(async () => {
    setPollCount((count) => count + 1);
    const pollError = await fetchSilent();
    if (pollError) {
      logError(pollError, "[InvoiceAgent] getInvoiceAgent poll");
    }
  }, [fetchSilent]);

  useSetInterval(poll, POLL_INTERVAL_MS, { disabled: !isAwaitingCheck(invoiceAgent, shownBeforeSave, pollCount) });

  if (invoiceUpdateAt !== prevInvoiceUpdateAt) {
    setPrevInvoiceUpdateAt(invoiceUpdateAt);
    setShownBeforeSave(invoiceAgent);
    setPollCount(0);
  }

  if (fetchError) {
    logError(fetchError, "[InvoiceAgent] getInvoiceAgent");
  }

  const checked = !fetchError && invoiceAgent?.state === "Checked" ? invoiceAgent : undefined;
  const summary = getSummary(invoiceAgent, fetchError !== undefined);
  const status = checked?.status;
  const overallStatusLabel = status && overallStatusLabels[status];
  const checks = checked?.checks ?? [];

  return (
    <Box
      sx={(t) => ({
        p: 2,
        border: "1px solid transparent",
        borderRadius: "8px",
        background: [
          `linear-gradient(100deg, ${makeOpaqueTintedBackgroundFromColor(t, blue[500], t.palette.background.default)}, ${makeOpaqueTintedBackgroundFromColor(t, purple[400], t.palette.background.default)} 55%, ${makeOpaqueTintedBackgroundFromColor(t, orange[500], t.palette.background.default)}) padding-box`,
          `${borderGradient} border-box`,
        ].join(", "),
      })}
    >
      <Stack direction="row" spacing={1.5}>
        <SparklesIcon sx={(t) => ({ width: 24, height: 24, color: makeModeAwareAccent(t, deepPurple) })} />

        <Stack spacing={1} sx={{ minWidth: 0, flexGrow: 1, alignItems: "flex-start" }}>
          <Stack spacing={0.5} sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
              <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
                <Typography variant="subtitle1">Invoice Agent</Typography>
                {status && overallStatusLabel && (
                  <StatusChip label={overallStatusLabel} color={checkStatusColors[status]} height="smaller" withDot />
                )}
              </Stack>
              <BetaLabel />
            </Box>
            {summary === undefined ? <Skeleton /> : <Typography>{summary}</Typography>}
          </Stack>

          {checks.length > 0 && (
            <>
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
                {checks.map((check) => {
                  const StatusIcon = checkStatusIcons[check.status];
                  return (
                    <StatusChip
                      key={check.id}
                      label={check.label}
                      color={checkStatusColors[check.status]}
                      icon={<StatusIcon />}
                    />
                  );
                })}
              </Stack>

              <ShowMoreLessButton
                expanded={expanded}
                expandButtonText="View details"
                collapseButtonText="Hide details"
                buttonColor="inherit"
                onClick={toggleExpanded}
              />
            </>
          )}
        </Stack>
      </Stack>

      {checks.length > 0 && (
        <Collapse in={expanded} unmountOnExit>
          <Stack spacing={2} sx={{ pt: 2 }}>
            {checks.map((check) => {
              const StatusIcon = checkStatusIcons[check.status];
              return (
                <Stack key={check.id} direction="row" spacing={1}>
                  <StatusIcon sx={{ color: checkStatusColors[check.status] }} />
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle2">{check.label}</Typography>
                    <Typography>{check.explanation}</Typography>
                  </Stack>
                </Stack>
              );
            })}

            <InvoiceAgentInvoicesTable
              title="Latest booked invoices from this vendor"
              invoices={checked?.recentInvoices ?? []}
            />
            <InvoiceAgentInvoicesTable
              title="Older invoices mentioned above"
              invoices={checked?.olderMentionedInvoices ?? []}
            />
          </Stack>
        </Collapse>
      )}
    </Box>
  );
};

export default InvoiceAgent;
